/**
 * Polyfill for TextTrackCue, VTTCue, and WebKitDataCue to handle non-finite values (Infinity, NaN)
 * for `endTime` and `startTime`.
 *
 * Modern browsers enforce restricted double for `TextTrackCue.prototype.endTime` and throw:
 * "TypeError: Failed to set the 'endTime' property on 'TextTrackCue': The provided double value is non-finite."
 * when Video.js / HLS stream metadata contains infinite duration or non-finite cues.
 */

export function polyfillTextTrackCue() {
  if (typeof window === 'undefined') return;

  const patchProperty = (proto, propName, fallbackCalc) => {
    if (!proto) return;

    let currentProto = proto;
    let descriptor = null;

    while (currentProto && !descriptor) {
      descriptor = Object.getOwnPropertyDescriptor(currentProto, propName);
      if (!descriptor) {
        currentProto = Object.getPrototypeOf(currentProto);
      }
    }

    if (!descriptor || !descriptor.set) return;

    const patchKey = `__patched_${propName}`;
    if (proto[patchKey]) return;

    const originalSet = descriptor.set;
    const originalGet = descriptor.get;

    Object.defineProperty(proto, propName, {
      get() {
        return originalGet ? originalGet.call(this) : this[`_${propName}`];
      },
      set(val) {
        let safeVal = val;
        if (typeof safeVal !== 'number' || !Number.isFinite(safeVal)) {
          safeVal = fallbackCalc(this);
        }
        try {
          originalSet.call(this, safeVal);
        } catch (e) {
          console.warn(`[Polyfill] Suppressed non-finite ${propName} set error:`, e);
        }
      },
      configurable: true,
      enumerable: descriptor.enumerable ?? true,
    });

    proto[patchKey] = true;
  };

  const prototypes = [
    window.TextTrackCue && window.TextTrackCue.prototype,
    window.VTTCue && window.VTTCue.prototype,
    window.WebKitDataCue && window.WebKitDataCue.prototype,
  ].filter(Boolean);

  prototypes.forEach((proto) => {
    patchProperty(proto, 'endTime', (cue) => {
      const start = cue && Number.isFinite(cue.startTime) ? cue.startTime : 0;
      return start + 3600;
    });

    patchProperty(proto, 'startTime', () => 0);
  });
}

// Run polyfill immediately on module load
polyfillTextTrackCue();
